// frontend/src/api/apiKeys.js
import client from "./client";

/**
 * API-key management — thin wrappers around /api/auth/api-keys.
 *
 * Keys are org-scoped (Phase 9): creation takes a name, a list of scopes, and
 * an optional expiry. The raw key (`key`) is present in the create/rotate
 * response exactly once and is never returned again.
 */

/** The scopes a key may be granted (mirrors the backend's VALID_SCOPES). */
export const VALID_SCOPES = [
  "codes:read",
  "codes:write",
  "analytics:read",
  "import",
  "export",
];

/**
 * List the active API keys for the current org, newest first.
 * @returns {Promise<Array<object>>} ApiKeyRead[] (never includes the raw key)
 */
export async function listApiKeys() {
  const response = await client.get("/api/auth/api-keys");
  return response.data;
}

/**
 * Create a new API key. The raw key is present in the response exactly once.
 * @param {{name: string, scopes?: string[], expiresAt?: string|null}} params
 * @returns {Promise<object>} ApiKeyCreated (includes `key`)
 */
export async function createApiKey({ name, scopes = [], expiresAt = null }) {
  const response = await client.post("/api/auth/api-keys", {
    name: name?.trim(),
    scopes,
    expires_at: expiresAt || null,
  });
  return response.data;
}

/**
 * Revoke (soft-disable) one of the org's API keys.
 * @param {string} id key UUID
 * @returns {Promise<void>}
 */
export async function revokeApiKey(id) {
  await client.delete(`/api/auth/api-keys/${id}`);
}

/**
 * Rotate a key: mint a replacement and grace-expire the original.
 * @param {string} id key UUID
 * @returns {Promise<object>} ApiKeyCreated (includes the new raw `key`)
 */
export async function rotateApiKey(id) {
  const response = await client.post(`/api/auth/api-keys/${id}/rotate`);
  return response.data;
}
